import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faBullhorn,
  faUsers,
  faShareAlt,
  faRocket,
  faGem,
  faPoll,
  faArrowRight,
  faCheckCircle,
  faStar,
  faClock,
  faShieldAlt,
  faLightbulb,
  faBullseye,
  faGlobe,
  faMobile,
  faDesktop,
  faHeart
} from '@fortawesome/free-solid-svg-icons';

const OurServices = () => {
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState('reporting');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const services = {
    reporting: {
      title: "Reporting & Analytics",
      description: "Track and generate detailed reports on social media campaigns and engagements with real-time analytics. Our comprehensive reporting tools provide actionable insights, performance metrics, and customizable dashboards to help you make data-driven decisions.",
      icon: faChartLine,
      image: "/assets/reporter.png",
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      features: [
        "Real-time analytics dashboard",
        "Custom report generation",
        "Performance metrics tracking",
        "ROI analysis and insights",
        "Automated data collection",
        "Trend analysis and forecasting"
      ],
      benefits: [
        "Make informed decisions with data",
        "Track campaign performance",
        "Identify growth opportunities",
        "Optimize marketing spend"
      ],
      link: "/ReportingServices"
    },
    advertising: {
      title: "Digital Advertising",
      description: "Create and manage effective advertising campaigns across multiple platforms. Our advanced advertising solutions include targeted audience segmentation, budget optimization, A/B testing capabilities, and cross-platform campaign management.",
      icon: faBullhorn,
      image: "/assets/advertiser.png",
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      features: [
        "Multi-platform campaign management",
        "Advanced audience targeting",
        "Budget optimization tools",
        "A/B testing capabilities",
        "Cross-platform analytics",
        "Automated bid management"
      ],
      benefits: [
        "Maximize ad spend efficiency",
        "Reach the right audience",
        "Increase conversion rates",
        "Scale campaigns effectively"
      ],
      link: "/AdvertisingServices"
    },
    influencing: {
      title: "Influencer Marketing",
      description: "Connect with influential voices and amplify your message to the right audience. Our influencer platform helps you identify, collaborate, and manage relationships with key industry influencers across various niches.",
      icon: faUsers,
      image: "/assets/influencing-services.png",
      color: "from-pink-500 to-pink-600",
      bgColor: "from-pink-50 to-pink-100",
      borderColor: "border-pink-200",
      features: [
        "Influencer discovery and vetting",
        "Campaign management tools",
        "Content approval workflows",
        "Performance tracking",
        "Relationship management",
        "ROI measurement"
      ],
      benefits: [
        "Build authentic brand partnerships",
        "Increase brand awareness",
        "Drive engagement and sales",
        "Access niche audiences"
      ],
      link: "/InfluencingServices"
    },
    socialMedia: {
      title: "Social Media Management",
      description: "Comprehensive social media management and engagement strategies. Our platform offers content scheduling, community management, social listening, and crisis management tools across all major platforms.",
      icon: faShareAlt,
      image: "/assets/social-media.png",
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      borderColor: "border-indigo-200",
      features: [
        "Content scheduling and planning",
        "Community management tools",
        "Social listening and monitoring",
        "Crisis management protocols",
        "Trend analysis and insights",
        "Automated posting and responses"
      ],
      benefits: [
        "Maintain consistent brand voice",
        "Engage with your audience",
        "Monitor brand mentions",
        "Respond to customer queries"
      ],
      link: "/SocialMediaServices"
    },
    campaignManagement: {
      title: "Campaign Management",
      description: "End-to-end campaign planning, execution, and monitoring services. From strategy development to performance analysis, our campaign management tools help you create, launch, and optimize marketing campaigns.",
      icon: faRocket,
      image: "/assets/capaign-manager.png",
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      features: [
        "Strategic campaign planning",
        "Timeline and resource management",
        "Multi-channel coordination",
        "Real-time performance tracking",
        "Budget allocation and monitoring",
        "Post-campaign analysis"
      ],
      benefits: [
        "Execute campaigns efficiently",
        "Coordinate multiple channels",
        "Track performance in real-time",
        "Optimize future campaigns"
      ],
      link: "/CampaignManagementServices"
    },
    brandLaunch: {
      title: "Brand Launch & Positioning",
      description: "Strategic brand launches and positioning in the market. Our brand launch services include market research, competitor analysis, brand identity development, and comprehensive launch strategy planning.",
      icon: faGem,
      image: "/assets/brand launch.png",
      color: "from-yellow-500 to-yellow-600",
      bgColor: "from-yellow-50 to-yellow-100",
      borderColor: "border-yellow-200",
      features: [
        "Market research and analysis",
        "Competitor analysis and positioning",
        "Brand identity development",
        "Launch strategy planning",
        "PR outreach and media relations",
        "Social media activation"
      ],
      benefits: [
        "Launch brands successfully",
        "Differentiate from competitors",
        "Build strong brand identity",
        "Generate buzz and awareness"
      ],
      link: "/BrandLaunchServices"
    },
    survey: {
      title: "Survey & Research",
      description: "Comprehensive survey and data collection services with advanced analytics capabilities. Create custom surveys, gather valuable feedback, and analyze responses with our intuitive research tools.",
      icon: faPoll,
      image: "/assets/survey.png",
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      borderColor: "border-red-200",
      features: [
        "Custom survey creation",
        "Automated data collection",
        "Real-time reporting",
        "Advanced analytics",
        "Trend analysis",
        "Statistical insights"
      ],
      benefits: [
        "Gather valuable customer insights",
        "Make data-driven decisions",
        "Understand market trends",
        "Improve products and services"
      ],
      link: "/SurveyServices"
    },
  };

  const handleServiceClick = (key) => {
    setActiveService(key);
  };

  const stats = [
    { number: "500+", label: "Campaigns Managed", icon: faRocket },
    { number: "50K+", label: "Active Users", icon: faUsers },
    { number: "99%", label: "Client Satisfaction", icon: faStar },
    { number: "24/7", label: "Support Available", icon: faClock }
  ];

  return (
    <div id="services" className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-16"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              OUR SERVICES
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Comprehensive <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Solutions</span> For Your Needs
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From reporting and analytics to brand launches, we provide end-to-end digital marketing solutions that drive results and grow your business.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Services Navigation */}
      <div className="w-full max-w-7xl mx-auto mb-12">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="flex flex-wrap justify-center gap-4"
        >
          {Object.entries(services).map(([key, service]) => (
            <motion.button
              key={key}
              variants={staggerItem}
              onClick={() => handleServiceClick(key)}
              className={`group relative px-6 py-4 rounded-2xl transition-all duration-300 ${
                activeService === key
                  ? `bg-gradient-to-r ${service.color} text-white shadow-xl scale-105`
                  : 'bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:shadow-lg border border-white/20'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  activeService === key ? 'bg-white/20' : `bg-gradient-to-r ${service.color}`
                }`}>
                  <FontAwesomeIcon 
                    icon={service.icon} 
                    className={`text-lg ${activeService === key ? 'text-white' : 'text-white'}`} 
                  />
                </div>
                <span className="font-semibold text-sm">{service.title}</span>
              </div>
              {activeService === key && (
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
              )}
            </motion.button>
          ))}
        </motion.div>
      </div>

      {/* Selected Service Details */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          key={activeService}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={`bg-gradient-to-br ${services[activeService].bgColor} rounded-3xl shadow-2xl border ${services[activeService].borderColor} overflow-hidden`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 sm:p-12">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-16 h-16 bg-gradient-to-r ${services[activeService].color} rounded-2xl flex items-center justify-center shadow-lg`}>
                  <FontAwesomeIcon icon={services[activeService].icon} className="text-white text-2xl" />
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
                    {services[activeService].title}
                  </h2>
                  <div className={`w-16 h-1 bg-gradient-to-r ${services[activeService].color} rounded-full`}></div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {services[activeService].description}
              </p>

              {/* Features */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services[activeService].features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className={`text-lg ${services[activeService].color.split(' ')[1].replace('to-', 'text-')}`} />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Benefits</h3>
                <div className="space-y-2">
                  {services[activeService].benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <FontAwesomeIcon icon={faStar} className={`text-sm ${services[activeService].color.split(' ')[1].replace('to-', 'text-')}`} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-6">
                <Link
                  to={services[activeService].link}
                  className={`inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r ${services[activeService].color} text-white rounded-2xl hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 font-semibold text-lg`}
                >
                  Learn More
                  <FontAwesomeIcon icon={faArrowRight} className="text-lg" />
                </Link>
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="flex items-center justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className={`w-80 h-80 bg-gradient-to-br ${services[activeService].color} rounded-3xl shadow-2xl flex items-center justify-center p-8`}>
                  <img
                    src={services[activeService].image}
                    alt={services[activeService].title}
                    className="w-full h-full object-contain filter brightness-0 invert"
                  />
                </div>
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon icon={faLightbulb} className="text-white text-sm" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon icon={faBullseye} className="text-white text-sm" />
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Our <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Impact</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Numbers that speak for our success and commitment to excellence
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={stat.icon} className="text-white text-xl" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OurServices;
